import React, { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import styles from "./styles.module.scss";
import { CustomMultiSelect, CustomSelect, Input, OptionType, CustomPhoneInput } from 'components/form';
import { CandidateBasicExpectationPayload, usePostCandidateBasicExpectation, useToast, BasicExpectation } from 'hooks';
import { employmentTypeOptions, experienceLevelOptions, getSelectedOption, workEnvironmentOptions, workModeOptions, yearsOfExperienceOptions } from 'components/edit-modals';
import { Import, TickIcon } from 'assets';
import { DocumentUpload } from 'components/chat';
import { fileToRawBase64 } from 'utils/filetoBase64';
import { ROLE_SOURCE_OPTIONS } from 'utils/roleSource';
import { StartAssessmentModal } from 'components/modal-variants/start-assessment';
import { ConfirmSalaryModal } from 'components/modal-variants/confirm-salary';
import { useNavigate } from 'react-router-dom';
import { openFullScreen } from 'helpers';
import { getSalarySanityWarning, SalarySanityWarning } from 'utils/salary';

interface ProfileFormProps {
  assessmentId: string | null;
  roleTitle?: string;
  salaryCurrency?: string;
  salaryTerm?: string;
  minSalary?: string | number | null;
  maxSalary?: string | number | null;
  requireCvUpload?: boolean;
  requirePortfolioUpload?: boolean;
  requireEmploymentType?: boolean;
  requireWorkMode?: boolean;
  requireExperienceLevel?: boolean;
  requireYearsOfExperience?: boolean;
  requireWorkEnvironment?: boolean;
  requireSalaryRange?: boolean;
}

export const foundRole: OptionType[] = ROLE_SOURCE_OPTIONS;

const ProfileFormComponent: React.FC<ProfileFormProps> = ({
  assessmentId,
  roleTitle,
  salaryCurrency: defaultCurrency,
  salaryTerm: defaultSalaryTerm,
  minSalary,
  maxSalary,
  requireCvUpload = true,
  requirePortfolioUpload = true,
  requireEmploymentType = true,
  requireWorkMode = true,
  requireExperienceLevel = true,
  requireYearsOfExperience = true,
  requireWorkEnvironment = true,
  requireSalaryRange = true,
}) => {
  const [isStartAssessmentModalOpen, setIsStartAssessmentModalOpen] = useState(false);
  const [salaryWarning, setSalaryWarning] = useState<SalarySanityWarning | null>(null);
  // The figure the candidate confirmed through the warning, and the one currently
  // being questioned. Kept as the values themselves rather than a boolean so that
  // editing the amount afterwards is re-checked instead of riding on a stale
  // confirmation. Refs because confirming re-runs submit in the same tick, before
  // a state update would have landed.
  const confirmedSalaryRef = useRef<number | null>(null);
  const pendingSalaryRef = useRef<number | null>(null);
  const [payload, setPayload] = useState<CandidateBasicExpectationPayload>();
  const [candidateEmail, setCandidateEmail] = useState<string>('');
  const { openToast } = useToast();
  const navigate = useNavigate();
  const { mutate: submitProfileInfo, isPending } = usePostCandidateBasicExpectation();
  const [cvFile, setCvFile] = useState<File | undefined>();
  // The employer sets the currency for the role and the candidate answers in it, so this is
  // fixed rather than a picker — same as the salary term already shown in the label below.
  const employerCurrency = (defaultCurrency || 'NGN').toUpperCase();
  const schema = yup.object().shape({
    fullName: yup.string().required('Full name is required'),
    email: yup.string().email('Invalid email').required('Email is required'),
    phoneNumber: yup.object().shape({
      code: yup.string().required('Country code is required'),
      number: yup
        .string()
        .required('Phone number is required')
        .matches(/^[0-9]+$/, 'Phone number must contain only digits')
        .min(8, 'Phone number must be at least 8 digits')
        .max(15, 'Phone number must not exceed 15 digits')
        .test('nigerian-length', 'Nigerian phone number must be exactly 10 digits', function(value) {
          const { code } = this.parent;
          if (code === '+234' && value) {
            return value.length === 10;
          }
          return true;
        }),
      value: yup.string().required('Phone number is required'),
    }).required('Phone number is required'),
    location: yup.string().required('Location is required'),
    experienceLevel: requireExperienceLevel
      ? yup.string().required('Experience level is required')
      : yup.string().optional(),
    yearsOfExperience: requireYearsOfExperience
      ? yup.string().required('Years of experience is required')
      : yup.string().optional(),
    employmentType: requireEmploymentType
      ? yup.string().required('Employment type is required')
      : yup.string().optional(),
    workMode: requireWorkMode
      ? yup.string().required('Preferred work mode is required')
      : yup.string().optional(),
    portfolioLink: (requirePortfolioUpload
      ? yup.string().required('Portfolio link is required')
      : yup.string())
      .max(255, 'Portfolio link must not exceed 255 characters')
      .test('is-url', 'Invalid URL', (value) => {
        if (!value) return true;
        try {
          const urlToTest = /^https?:\/\//i.test(value) ? value : `https://${value}`;
          new URL(urlToTest);
          return true;
        } catch {
          return false;
        }
      }),
    workEnvironment: requireWorkEnvironment
      ? yup.string().required('Work environment is required')
      : yup.string().optional(),
    salaryCurrency: requireSalaryRange
      ? yup.string().required('Currency is required')
      : yup.string().optional(),
    salaryAmount: requireSalaryRange
      ? yup.number().required('Salary expectation is required').positive('Must be a positive number').nullable()
      : yup.number().nullable().optional(),
    salaryPeriod: requireSalaryRange
      ? yup.string().required('Salary period is required')
      : yup.string().optional(),
    ...(requireCvUpload && { cvResume: yup.mixed().required('CV/Resume is required') }),
    whereSawRole: yup.string().required('Please specify where you saw this role'),
  });

  const { register, handleSubmit, control, reset, formState: { errors }, setValue, watch, trigger } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      fullName: '',
      email: '',
      phoneNumber: { code: '+234', number: '', value: '' },
      location: '',
      experienceLevel: '',
      yearsOfExperience: '',
      employmentType: '',
      workMode: '',
      portfolioLink: '',
      workEnvironment: '',
      salaryCurrency: employerCurrency,
      salaryAmount: null,
      salaryPeriod: defaultSalaryTerm || 'monthly',
      cvResume: '',
      whereSawRole: ''
    }
  });

  // The assessment details load after this form mounts, so defaultValues can capture the 'NGN'
  // fallback before the employer's real currency arrives. Re-sync once it does.
  useEffect(() => {
    setValue('salaryCurrency', employerCurrency);
  }, [employerCurrency, setValue]);

  const handleImportCV = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setCvFile(file);
    }
  }

  const onSubmit = async (data) => {
    // Questioned before the CV is read, so returning to fix the amount doesn't
    // cost a re-encode of the file.
    if (requireSalaryRange && data.salaryAmount !== confirmedSalaryRef.current) {
      const warning = getSalarySanityWarning(data.salaryAmount, {
        min: minSalary,
        max: maxSalary,
        currency: employerCurrency,
      });

      if (warning) {
        pendingSalaryRef.current = data.salaryAmount;
        setSalaryWarning(warning);
        return;
      }
    }

    let resumeBase64 = '';
    if (cvFile) {
      try {
        resumeBase64 = await fileToRawBase64(cvFile);
      } catch (error) {
        console.error('Failed to convert resume to base64:', error);
        openToast({
          show: true,
          heading: 'Submission Failed',
          text: 'Failed to process resume file',
          type: 'error',
          timeOut: 5000,
        });
        return;
      }
    }
    if (assessmentId) {
      const payload: CandidateBasicExpectationPayload = {
        assessment: assessmentId,
        full_name: data.fullName,
        email: data.email,
        portfolio: data.portfolioLink,
        resume: resumeBase64,
        phone_number: data.phoneNumber.value,
        minutes_left: 60,
        role_source: data.whereSawRole,
        basic_expectation: {
          location: data.location,
          ...(requireExperienceLevel && { exp_level: data.experienceLevel }),
          ...(requireYearsOfExperience && { exp_years: data.yearsOfExperience }),
          ...(requireEmploymentType && { emp_type: data.employmentType }),
          ...(requireWorkMode && { work_mode: data.workMode }),
          ...(requireSalaryRange && { salary_expectation: data.salaryAmount }),
          ...(requireWorkEnvironment && { exp_environment: data.workEnvironment }),
          role_source: data.whereSawRole,
        },
      };
      setPayload(payload);
      setCandidateEmail(data.email);
      setIsStartAssessmentModalOpen(true);
    }
  };

  const handleSubmitProfileInfo = () => {
    if (payload && candidateEmail) {
      // Enter fullscreen synchronously, while we're still inside the confirm
      // button's user-gesture. requestFullscreen is rejected if called any later
      // (e.g. from the async onSuccess below) because the gesture has lapsed by
      // the time the POST resolves. The fullscreen state persists across the
      // client-side navigation into the assessment. Skipped on touch devices
      // (unreliable there, and proctoring doesn't enforce it); no-ops on Safari.
      const isTouchDevice =
        'ontouchstart' in window ||
        (navigator.maxTouchPoints ?? 0) > 0 ||
        Boolean(window.matchMedia?.('(pointer: coarse)').matches);
      if (!isTouchDevice) openFullScreen();

      // Save email to localStorage for use in assessment
      localStorage.setItem('candidate_email', candidateEmail);

      // Submit the profile info
      submitProfileInfo(payload, {
        onSuccess: (data) => {
          // Save assessment response data from the submission response
          if (data.data?.id) {
            localStorage.setItem('assessment_response_id', data.data.id.toString());
          }

          // Save end_time, duration_minutes_left, and other important data
          if (data.data) {
            localStorage.setItem('assessment_response_data', JSON.stringify({
              id: data.data.id,
              end_time: data.data.end_time,
              duration_minutes: data.data.duration_minutes,
              duration_minutes_left: data.data.duration_minutes_left,
              status: data.data.status
            }));
          }

          openToast({
            show: true,
            heading: 'Success',
            text: 'Candidate basic expectation submitted successfully!',
            type: 'info',
            timeOut: 5000,
          });
          navigate(`/take-assessment/${assessmentId}`);
          reset();
          setIsStartAssessmentModalOpen(false);
        },
        onError: (err: any) => {
          const errorMessage = err?.response?.data?.message || 'Failed to submit candidate basic expectation';
          openToast({
            show: true,
            heading: 'Submission Failed',
            text: errorMessage,
            type: 'error',
            timeOut: 5000,
          });
        },
      });
    }
  };

  const handleStartAssessment = () => {
    setIsStartAssessmentModalOpen(false);
    navigate(`/take-assessment/${assessmentId}`);
  };

  const handleEditSalary = () => {
    setSalaryWarning(null);
    const el = document.querySelector('[name="salaryAmount"]') as HTMLInputElement | null;
    // Focus first with the browser's own jump suppressed, so the scroll below
    // stays smooth rather than being pre-empted by it.
    el?.focus({ preventScroll: true });
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleConfirmSalary = () => {
    confirmedSalaryRef.current = pendingSalaryRef.current;
    setSalaryWarning(null);
    // Pick the submission back up where the warning interrupted it.
    handleSubmit(onSubmit)();
  };

  return (
    <>
      <ConfirmSalaryModal
        isOpen={!!salaryWarning}
        warning={salaryWarning}
        onEdit={handleEditSalary}
        onContinue={handleConfirmSalary}
      />
      <StartAssessmentModal
        isOpen={isStartAssessmentModalOpen}
        onClose={() => setIsStartAssessmentModalOpen(false)}
        onSave={handleSubmitProfileInfo}
        // handleDetailsConfirmation={handleSubmitProfileInfo}
        isLoading={isPending}
      />
      <div className={styles.profileFormContainer}>
        <p>Great! Fill the forms below and we can move right into the assessment.</p>
        <div className={styles.divider}></div>
        <div className={styles.headerSection}>
          <h2 className={styles.headerText}>Personal Information</h2>
        </div>
        <div className={styles.formGroup}>
          <Input
            label="Full name"
            name="fullName"
            placeholder="Enter your full name"
            register={register}
            validatorMessage={errors.fullName?.message}
            required
            className={styles.inputClass}
            parentClassName={styles.parentInputClass}
            showRequired
          />
          <Input
            label="Email address"
            name="email"
            placeholder="Enter your email address"
            register={register}
            validatorMessage={errors.email?.message}
            required
            className={styles.inputClass}
            parentClassName={styles.parentInputClass}
            showRequired
          />
        </div>
        <div className={styles.formGroup}>
          <CustomPhoneInput
            label="Phone number"
            placeholder="Enter your phone number"
            validatorMessage={
              (errors.phoneNumber as any)?.number?.message?.toString() ||
              (errors.phoneNumber as any)?.code?.message?.toString() ||
              (errors.phoneNumber as any)?.value?.message?.toString() ||
              ''
            }
            onChange={({ code, number, value }) => {
              setValue('phoneNumber', { code, number, value });
              trigger('phoneNumber');
            }}
            value={watch('phoneNumber')?.value || ''}
            parentClassName={styles.parentInputClass}
            showRequired
          />
          <Input
            label="Location"
            name="location"
            placeholder="e.g Ogun State, Nigeria"
            register={register}
            validatorMessage={errors.location?.message}
            required
            className={styles.inputClass}
            parentClassName={styles.parentInputClass}
            showRequired
          />
        </div>
        <div className={styles.divider}></div>
        <div className={styles.headerSection}>
          <h2 className={styles.headerText}>Work Information</h2>
        </div>
        {requireExperienceLevel && (
          <div className={styles.formGroup}>
            <Controller
              name="experienceLevel"
              control={control}
              render={({ field }) => (
                <CustomSelect
                  label={`Experience Level as a ${roleTitle || 'candidate'}`}
                  placeholder="Select the experience level"
                  options={experienceLevelOptions}
                  value={getSelectedOption(experienceLevelOptions, field.value as string | undefined)}
                  onChange={(option) => field.onChange(option?.value || undefined)}
                  validatorMessage={errors.experienceLevel?.message || ''}
                  name="experienceLevel"
                  parentClassName={styles.selectField}
                  inputClass={styles.selectInput}
                  showRequired
                />
              )}
            />
          </div>
        )}
        {requireYearsOfExperience && (
          <div className={styles.formGroup}>
            <Controller
              name="yearsOfExperience"
              control={control}
              render={({ field }) => (
                <CustomSelect
                  label={`How many years of experience do you have working as a ${roleTitle || 'candidate'}`}
                  placeholder="Select years of experience"
                  options={yearsOfExperienceOptions}
                  value={getSelectedOption(yearsOfExperienceOptions, field.value as string | undefined)}
                  onChange={(option) => field.onChange(option?.value || undefined)}
                  validatorMessage={errors.yearsOfExperience?.message || ''}
                  name="yearsOfExperience"
                  parentClassName={styles.selectField}
                  inputClass={styles.selectInput}
                  showRequired
                />
              )}
            />
          </div>
        )}
        {(requireEmploymentType || requireWorkMode) && (
          <div className={styles.formGroup}>
            {requireEmploymentType && (
              <Controller
                name="employmentType"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    label="Employment Type"
                    placeholder="Select the employment type"
                    options={employmentTypeOptions}
                    value={getSelectedOption(employmentTypeOptions, field.value as string | undefined)}
                    onChange={(option) => field.onChange(option?.value || undefined)}
                    validatorMessage={errors.employmentType?.message || ''}
                    name="employmentType"
                    parentClassName={styles.selectField}
                    inputClass={styles.selectInput}
                    showRequired
                  />
                )}
              />
            )}
            {requireWorkMode && (
              <Controller
                name="workMode"
                control={control}
                render={({ field }) => (
                  <CustomSelect
                    label="Preferred Work Mode"
                    placeholder="Select the work mode"
                    options={workModeOptions}
                    value={getSelectedOption(workModeOptions, field.value as string | undefined)}
                    onChange={(option) => field.onChange(option?.value || undefined)}
                    validatorMessage={errors.workMode?.message || ''}
                    name="workMode"
                    parentClassName={styles.selectField}
                    inputClass={styles.selectInput}
                    showRequired
                  />
                )}
              />
            )}
          </div>
        )}
        {requirePortfolioUpload && (
          <div className={styles.formGroup}>
            <Input
              label="Portfolio or relevant link"
              name="portfolioLink"
              placeholder="Input link here"
              register={register}
              validatorMessage={errors.portfolioLink?.message}
              className={styles.inputClass}
              parentClassName={styles.parentInputClass}
              showRequired
            />
          </div>
        )}
        {requireWorkEnvironment && (
          <div className={styles.formGroup}>
            <Controller
              name="workEnvironment"
              control={control}
              render={({ field }) => (
                <CustomSelect
                  label="Based on your previous employment history, what type of work environment have you experienced?"
                  placeholder="Select work environment"
                  options={workEnvironmentOptions}
                  value={getSelectedOption(workEnvironmentOptions, field.value as string | undefined)}
                  onChange={(option) => field.onChange(option?.value || undefined)}
                  validatorMessage={errors.workEnvironment?.message || ''}
                  name="workEnvironment"
                  parentClassName={styles.selectField}
                  inputClass={styles.selectInput}
                  showRequired
                />
              )}
            />
          </div>
        )}
        {requireSalaryRange && (
          <div className={styles.formGroup}>
            <div className={styles.salaryGroup}>
              <label>
                Salary Expectations ({defaultSalaryTerm || 'monthly'}) <span className='text-red-400'>*</span>
              </label>
              <div className='flex gap-2 w-full flex-wrap md:flex-nowrap md:gap-2 border border-[#01216980] rounded-md p-1.5 mt-3'>
                <span className='flex items-center justify-center w-[70px] shrink-0 bg-[#012169] rounded px-2.5 py-2 text-sm text-[#FFFFFF]'>
                  {employerCurrency}
                </span>
                <div className='flex items-center gap-2 flex-grow'>
                  <Input
                    label=""
                    name="salaryAmount"
                    placeholder="Enter amount"
                    type="number"
                    register={register}
                    validatorMessage={errors.salaryAmount?.message}
                    required
                    className={styles.rangeInput}
                    parentClassName={`${styles.rangeInputParent} !mb-0`}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        {requireCvUpload && (
          <div className={styles.formGroup}>
            <div className={styles.cvGroup}>
              <label className={styles.cvLabel}>
                Upload CV or Resume <span className='text-red-400'>*</span>
              </label>
              <div className={styles.dropZone}>
                <div className={styles.uploadIcon}>
                  <div className={styles.Icon}>
                    <Import />
                  </div>
                </div>
                <p className={styles.dropText}>{cvFile ? 'Upload another file to replace' : 'Select a pdf or doc file to upload'}</p>
                <p className={styles.dropSubtext}>or drag and drop it here</p>
                <input
                  type="file"
                  accept="application/pdf,application/msword"
                  onChange={handleImportCV}
                  className={styles.fileInput}
                />
              </div>
              {cvFile && (
                <DocumentUpload
                  files={[cvFile]}
                  onRemove={() => setCvFile(undefined)}
                />
              )}
            </div>
          </div>
        )}
        <div className={styles.formGroup}>
          <Controller
            name="whereSawRole"
            control={control}
            render={({ field }) => (
              <CustomSelect
                label="Where did you see this role?"
                placeholder="Select where you saw this role"
                options={foundRole}
                value={getSelectedOption(foundRole, field.value)}
                onChange={(option) => field.onChange(option?.value || undefined)}
                validatorMessage={errors.whereSawRole?.message || ''}
                name="whereSawRole"
                parentClassName={styles.selectField}
                inputClass={styles.selectInput}
                showRequired
              />
            )}
          />
        </div>

        <div className={styles.divider}></div>
        <div className={styles.footerSection}>
          <p className={styles.footerText}>
            Confirm your detail before you start the assessment.
          </p>
        </div>
        <div className={styles.divider}></div>

        <div className={styles.footer}>
          <button
            className={styles.submit}
            type="submit"
            onClick={handleSubmit(onSubmit, (formErrors) => {
              openToast({
                show: true,
                heading: 'Incomplete Form',
                text: 'Please fill in all required fields correctly before continuing.',
                type: 'error',
                timeOut: 5000,
              });
              const firstErrorField = Object.keys(formErrors)[0];
              if (firstErrorField) {
                const el = document.querySelector(`[name="${firstErrorField}"]`) as HTMLElement | null;
                el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            })}
          >
            {isPending ? 'Pending...' : 'Start Assessment'} <div className={styles.optionIcon}><TickIcon /></div>
          </button>
        </div>
      </div>
    </>

  );
};

export { ProfileFormComponent };
